import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import PropTypes from 'prop-types';

import useDataSource from "../../../../hooks/useDataSource";

import {dataAddFilter, dataRemoveFilter, filterUpdateConditionAction} from "../../../../redux/data/dataActions";
import { styled, Tabs, Tab, LinearProgress, Box } from '@mui/material';

const PREFIX = 'TabFilter';

const classes = {
    root: `${PREFIX}-root`,
    tab: `${PREFIX}-tab`,
    linearProgress: `${PREFIX}-linearProgress`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: "100%",
        width: "100%",
        overflow: "hidden",
		backgroundColor: theme.palette.background.default
	},

    [`& .${classes.tab}`]: {
		backgroundColor: theme.palette.background.default
	},

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    }
}));

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    // NOTE: We may still want to allow
    // Parent/Paper stying of the tab however this is
    // currently not implemented
    return (
        <div
            style={{height: 'calc(100% - 60px)', width: '100%', overflow: 'auto'}}
            id={`wrapped-tabpanel-${index}`}
            aria-labelledby={`wrapped-tab-${index}`}
            {...other}
        >
            <Box style={{height: '100%', width: '100%', padding: '0px'}}>{children}</Box>
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired,
};

export default function TabFilter({ id, configuration, parseComponent, level }) {
    const dispatch = useDispatch();

    const alignTop = configuration.element?.alignTop !== undefined ? configuration.element.alignTop : true;
    const removeFilterOnRouteChange = configuration.element?.removeFilterOnRouteChange !== undefined ? configuration.element.removeFilterOnRouteChange : true;
    const hiddenFilter = configuration.element?.hiddenFilter ? configuration.element.hiddenFilter : true;
    const filterField = configuration.xFilter?.field ? configuration.xFilter.field : '';
    const dataSourceName = configuration.dataSource?.name ? configuration.dataSource?.name : '';

    // App State
    const dataSource = useDataSource(dataSourceName);
    const filters = useSelector(state => state.data.filters);

    // Component State
    const [tabComponents, setTabComponents] = useState(null);
    const [tabPanel, setTabPanel] = useState(null);
    const [cachedData, setCachedData] = useState(null);
    // Here we track the selected tab and the value that corresponds to the selection
    // in order to update addDataFilter
    const [tabValue, setTabValue] = useState(-2112);        // Nothing selected by default
    const [filterValue, setFilterValue] = useState(null);   // We read in the existing value or set to -2112 if nothing - this is required bc the setFilterValue does not update before the next hook fires
    const [tabAlignTop, setTabAlignTop] = useState(!!alignTop);

    useEffect(() => {

        if (configuration.components.length > 0) {
            setTabPanel(<TabPanel key={configuration.components.length} index={0} value={configuration.components.length}>{parseComponent(configuration.components[0], false, level, false, null, null, null)}</TabPanel>);
        } else {
            setTabPanel(<TabPanel></TabPanel>);
        }

        // Sync to changes from other components i.e.
        // set our internal filter value
        const componentFilters = filters.find(f => {
            return f.id === id
        });

        if (componentFilters) {
            // TODO: Add utility or store in state since this
            // only assumes single condition - for now we'll use
            // local context however sharing across components
            // TBD will share same f.id
            // HACK:
            let eqCondition = componentFilters.conditions[0];
            eqCondition = eqCondition.substr(eqCondition.indexOf("eq '") + 4);
            eqCondition = eqCondition.substr(0, eqCondition.length - 1);
            eqCondition = eqCondition.toString().toLowerCase();

            setFilterValue(eqCondition);
        } else {
            setFilterValue(-2112);
        }

        // Remove filter when user navigates away?
        if (removeFilterOnRouteChange) {
            return function cleanup() {
                const filter = '{0}-{1}'.format(dataSourceName.toLowerCase(), filterField.toLowerCase())
                dispatch(dataRemoveFilter(filter));
            };
        }

    }, []);

    useEffect(() => {

        // Tabs are responsive to filters i.e.
        // a change in Action Plan
        if (dataSource.data && dataSource.fetched && filterValue) {
            // Setup base data as filtered results or a
            // baseline if included
            let data = dataSource.data;

            // Find my baseline
            const baseline = dataSource.baselines.find(d => {
                return d.id.toLowerCase() === id.toLowerCase()
            });

            if (baseline) {
                let useBaseline = true;

                // If there is a baseline then we use
                // that value IF we can highlight
                if (filterValue !== -2112) {
                    if (!baseline.result.find(d => {
                        return d[filterField].toString().toLowerCase() === filterValue.toString().toLowerCase()
                    })) {
                        useBaseline = false;
                    }
                }

                if (useBaseline) {
                    data = baseline.result;
                }
            }
            setCachedData(data);

            let firstFilterValue = null;
            const tcs = [];
            for (let i = 0; i < data.length; i++) {
                if (!firstFilterValue)
                    firstFilterValue = data[i][filterField];

                tcs.push(<Tab key={i} label={data[i][filterField]}></Tab>);
            }

            setTabComponents(tcs);

            // Select tab
            const componentFilters = filters.find(f => {
                return f.id.toLowerCase() === id.toLowerCase() && f.field.toLowerCase() === filterField.toLowerCase();
            });

            let tabValueSet = false;
            if (componentFilters) {
                // TODO: Add utility or store in state since this
                // only assumes single condition - for now we'll use
                // local context however sharing across components
                // TBD will share same f.id
                // HACK:
                let eqCondition = componentFilters.conditions[0];
                eqCondition = eqCondition.substr(eqCondition.indexOf("eq '") + 4);
                eqCondition = eqCondition.substr(0, eqCondition.length - 1);
                eqCondition = eqCondition.toString().toLowerCase();

                if (filterValue !== eqCondition) {
                    // We're out of sync so we need to update the tab and the filter value

                    // Set the tab value to the position of eqCondition
                    for (let i = 0; i < data.length; i++) {
                        const d = data[i];
                        if (d[filterField].toString().toLowerCase() === eqCondition) {
                            tabValueSet = true;
                            setTabValue(i);
                            setFilterValue(eqCondition);
                        }
                    }
                } else {
                    tabValueSet = syncTabWithFilterValue(data);
                }
            }

            // Auto-select first tab
            if (firstFilterValue && !tabValueSet) {
                dispatch(dataAddFilter(id, filterField, " eq '{0}'".format(firstFilterValue), filterUpdateConditionAction.REPLACE, true, hiddenFilter));
                setTabValue(0);
                setFilterValue(firstFilterValue.toString().toLowerCase());
            }
        }

    }, [dataSource.data, dataSource.fetching, dataSource.fetched, dataSource.error, filterValue]);

    useEffect(() => {

        // Sync to changes from other components
        const componentFilters = filters.find(f => {
            return f.id === id
        });

        if (componentFilters) {
            // TODO: Add utility or store in state since this
            // only assumes single condition - for now we'll use
            // local context however sharing across components
            // TBD will share same f.id
            // HACK:
            let eqCondition = componentFilters.conditions[0];
            eqCondition = eqCondition.substr(eqCondition.indexOf("eq '") + 4);
            eqCondition = eqCondition.substr(0, eqCondition.length - 1);
            eqCondition = eqCondition.toString().toLowerCase();

            setFilterValue(eqCondition);
        } else {
            setFilterValue(-2112);
        }

    }, [filters]);

    const syncTabWithFilterValue = (data) => {
        for (let i = 0; i < data.length; i++) {
            const d = data[i];
            if (d[filterField].toString().toLowerCase() === filterValue.toString().toLowerCase()) {
                setTabValue(i);
                return true;
            }
        }
        return false;
    }

    const handleChange = (event, newValue) => {

        setTabValue(newValue);
        setFilterValue(cachedData[newValue][filterField]);
        dispatch(dataAddFilter(id, filterField, " eq '{0}'".format(cachedData[newValue][filterField]), filterUpdateConditionAction.REPLACE, true, hiddenFilter));
    };

    return (
        <Root className={classes.root}>
            {!tabComponents && tabValue === -2112 &&
            <LinearProgress className={classes.linearProgress}/>
            }
            {tabComponents &&
                <React.Fragment>
                {!tabAlignTop &&
                    <React.Fragment>
                        {tabPanel}
                    </React.Fragment>
                }
                    <Tabs
                        className={classes.tab}
                        value={tabValue}
                        indicatorColor="primary"
                        textColor="primary"
                        onChange={handleChange}
						scrollButtons={true}
						allowScrollButtonsMobile
						selectionFollowsFocus
                        variant="scrollable"
                        aria-label="disabled tabs example"
                    >
                        {tabComponents}
                    </Tabs>
                {tabAlignTop &&
                    <React.Fragment>
                        {tabPanel}
                    </React.Fragment>
                }
                </React.Fragment>
            }
        </Root>
    );
}
